import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  LinearProgress,
} from '@mui/material';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const PerformanceOverview = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get('/api/results/stats/overview');
        setStats(res.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching performance data:', error);
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Container>
        <Typography variant="h6">Loading...</Typography>
      </Container>
    );
  }

  if (!stats || stats.totalTests === 0) {
    return (
      <Card sx={{ mt: 2, p: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Performance Overview
          </Typography>
          <h2>Performance Overview</h2>
          <p>You haven't taken any tests yet. <Link to="/tests" className="btn">Take a test now</Link></p>
        </CardContent>
      </Card>
    );
  }

  // Prepare data for overall performance pie chart
  const pieData = {
    labels: ['Correct', 'Incorrect'],
    datasets: [
      {
        data: [stats.averageScore, 100 - stats.averageScore],
        backgroundColor: ['#4caf50', '#f44336'], // Keep green/red for correct/incorrect
        borderColor: ['#ffffff', '#ffffff'],
        borderWidth: 2,
      },
    ],
  };

  // Prepare data for subject-wise performance bar chart
  const subjects = Object.keys(stats.subjectWisePerformance);
  const subjectScores = subjects.map(subject => stats.subjectWisePerformance[subject].averageScore);

  const barData = {
    labels: subjects,
    datasets: [
      {
        label: 'Average Score (%)',
        data: subjectScores,
        backgroundColor: '#1a73e8', // Primary blue
        borderColor: '#1557b0',
        borderWidth: 1,
      },
    ],
  };

  const barOptions = {
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
      },
    },
    plugins: {
      legend: { display: false }, // Hide legend for simplicity
      title: {
        display: true,
        text: 'Subject-wise Performance',
        padding: { top: 10, bottom: 20 },
        font: { size: 16 },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom>
        Performance Overview
      </Typography>

      {/* Overall Stats Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Overall Performance
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5">{stats.totalTests}</Typography>
                <Typography variant="body2">Tests Taken</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5">{stats.averageScore.toFixed(2)}%</Typography>
                <Typography variant="body2">Average Score</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5">{stats.highestScore.toFixed(2)}%</Typography>
                <Typography variant="body2">Highest Score</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5">{stats.lowestScore.toFixed(2)}%</Typography>
                <Typography variant="body2">Lowest Score</Typography>
              </Card>
            </Grid>
          </Grid>
          <Box sx={{ height: 300, width: '100%', maxWidth: 400, mx: 'auto', mt: 4 }}>
            <Pie
              data={pieData}
              options={{ responsive: true, maintainAspectRatio: true, plugins: { legend: { position: 'bottom' } } }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Subject Performance Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Subject-wise Performance
          </Typography>
          <Grid container spacing={3}>
            {subjects.map((subject) => {
              const subjectData = stats.subjectWisePerformance[subject];
              return (
                <Grid item xs={12} sm={6} md={4} key={subject}>
                  <Card sx={{ p: 2, textAlign: 'left', cursor: 'pointer' }} onClick={() => navigate(`/subjects/${encodeURIComponent(subject)}`)}>
                    <Typography variant="h5" color="primary">{subject}</Typography>
                    <Typography variant="body2">Average: {subjectData.averageScore.toFixed(2)}%</Typography>
                    <Typography variant="body2">Tests: {subjectData.totalTests}</Typography>
                    <LinearProgress variant="determinate" value={subjectData.averageScore} sx={{ mt: 1 }} />
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {/* Bar Chart */}
          <div style={{ height: '400px', marginTop: '3rem' }}>
            <Bar data={barData} options={barOptions} />
          </div>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="contained" color="secondary" component={Link} to="/progress">
              View Progress Tracker
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PerformanceOverview;
